import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, {
  forwardRef,
  useMemo,
  type ChangeEvent,
  type WheelEventHandler,
} from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { Icon, IconLoading } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import {
  mergeProps,
  stripInvalidPropsForElement,
} from "../../utils/component-props";
import { InputWithLabels } from "../_components/InputWithLabels";
import styles from "./TextInput.module.scss";
import type { ControlledTextInputProps, TextInputProps } from "./types";

const cx = classNames.bind(styles);

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      id,
      name,
      type = "text",
      formatType = "string",
      value = "",
      placeholder = "",
      onChange,
      onBlur,
      colorScheme = "light",
      showIcon = false,
      disabled = false,
      isDisabled = false,
      isLoading = false,
      isBusy = false,
      invalidMessage,
      "aria-invalid": ariaInvalid,
      error,
      unit,
      leftSlot,
      rightSlot,
      flairSlot,
      step,
      pattern,
      className = "",
      style,
      inputStyle,
      ...restProps
    },
    ref
  ) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      // apply formatting as-you-type
      switch (formatType) {
        // if forced to be an integer...
        case "integer": {
          const currValueAsNumber = parseFloat(value);
          const newValueAsNumber = parseFloat(newValue);

          if (isNaN(newValueAsNumber)) {
            return onChange?.(newValue);
          }

          // strip the integer part out of the number - while respecting 'number' input type accessibility controls and how they manipulate the number
          const integerPartOfNumber = Math.trunc(
            newValueAsNumber <
              currValueAsNumber + (newValueAsNumber < 0 ? -0.5 : 0.5)
              ? Math.floor(newValueAsNumber)
              : newValueAsNumber >
                  currValueAsNumber + (newValueAsNumber < 0 ? -0.5 : 0.5)
                ? Math.ceil(newValueAsNumber)
                : newValueAsNumber
          );

          // @ts-expect-error returning a number on purpose to satisfy external constraints
          return onChange?.(Number(integerPartOfNumber));
        }
        case "number": {
          const newValueAsNumber = parseFloat(newValue);

          if (isNaN(newValueAsNumber)) {
            return onChange?.(newValue);
          }

          // @ts-expect-error returning a number on purpose to satisfy external constraints
          return onChange?.(Number(newValue));
        }
        default: {
          return onChange?.(newValue);
        }
      }
    };

    const handleNumberTypeWheel: WheelEventHandler<HTMLInputElement> = (
      evt
    ) => {
      if (type !== "number") return;

      // blur the input
      evt.currentTarget.blur();

      // don't scroll the page
      evt.stopPropagation();
    };

    return (
      <Skeleton
        isLoading={isLoading}
        isBusy={isBusy}
      >
        <div
          className={cx("container", className)}
          style={style}
        >
          {leftSlot && <div className={cx("left-slot")}>{leftSlot?.({})}</div>}
          <div className={cx("inputContainer")}>
            {type === "search" ? (
              <div className={cx("icon")}>
                <Icon
                  size={16}
                  weight="thin"
                  variant="magnifying-glass"
                />
              </div>
            ) : null}
            <input
              id={id}
              name={name}
              className={cx("input", {
                "input--invalid":
                  ariaInvalid === true || !!invalidMessage || !!error,
                "input--disabled": isDisabled || disabled,
                "input--truncated": showIcon,
                "input--with-right-adornment":
                  unit !== undefined || !!rightSlot,
                "input--with-left-adornment": !!leftSlot,
                "input--with-icon": type === "search",
              })}
              style={inputStyle}
              placeholder={placeholder}
              ref={ref}
              onChange={handleChange}
              onBlur={onBlur}
              value={value}
              type={type}
              step={type === "number" ? step : undefined}
              disabled={isDisabled || disabled}
              pattern={pattern}
              aria-busy={isBusy}
              aria-invalid={ariaInvalid}
              {...(type === "number" ? { onWheel: handleNumberTypeWheel } : {})}
              {...stripInvalidPropsForElement(restProps, "input")}
            />
            {(isBusy || flairSlot) && (
              <div className={cx("flair")}>
                {isBusy && (
                  <IconLoading
                    speed="fast"
                    size={10}
                  />
                )}
                {flairSlot?.({})}
              </div>
            )}
          </div>
          {(unit || rightSlot) && (
            <div
              className={cx("right-slot", {
                "right-slot--with-unit": !!unit,
              })}
            >
              {unit}
              {rightSlot?.({})}
            </div>
          )}
        </div>
      </Skeleton>
    );
  }
);

TextInput.displayName = "TextInput";

const ControlledTextInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledTextInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Text input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
      disabled: field.disabled,
      name: field.name,
      ref: field.ref,
    });
  }, [
    restProps,
    field.onChange,
    field.onBlur,
    field.disabled,
    field.name,
    field.ref,
  ]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ leftSlot, flairSlot, invalidMessageId, labelId, inputId }) => (
        <TextInput
          id={inputId}
          labelledById={labelId}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          leftSlot={leftSlot}
          flairSlot={flairSlot}
          {...mergedProps}
          value={field.value}
        />
      )}
    </InputWithLabels>
  );
};

ControlledTextInput.displayName = "ControlledTextInput";

export { ControlledTextInput };
export default TextInput;
